/**
 * IW Cart drawer — Section Rendering API + site-wide add-to-cart capture
 * Config: snippets/iw-cart-drawer-config.liquid (money format, labels) +
 *         data-* on [data-iw-cart-drawer-root] after section renders/refreshes
 */
(function () {
  'use strict';

  const NAME = 'IWCartDrawer';

  function cfg() {
    const el = document.querySelector('[data-iw-cart-drawer-root]');
    const w = window.IW_CART_DRAWER || {};
    const o = Object.assign({}, w);
    if (el) {
      o.sectionId = el.getAttribute('data-section-id') || w.sectionId;
      o.upsellMode = el.getAttribute('data-upsell-mode') || w.upsellMode;
      o.recommendationIntent =
        el.getAttribute('data-recommendation-intent') || w.recommendationIntent;
      o.dynamicLimit = parseInt(el.getAttribute('data-dynamic-limit') || '3', 10) || 3;
    }
    return o;
  }

  function rootPath() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    return '/';
  }

  function formatMoney(cents) {
    const c = Math.round(Number(cents) || 0);
    const fmt = cfg().moneyFormat || '${{amount}}';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(c, fmt);
    }
    const amount = (c / 100).toFixed(2);
    if (/\{\{\s*amount\s*\}\}/.test(fmt)) {
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  function renderUpsellPriceMarkup(priceCents, compareAtCents) {
    const price = Math.round(Number(priceCents) || 0);
    const compare = Math.round(Number(compareAtCents) || 0);
    const sale = compare > price;
    const cur = formatMoney(price);
    if (!sale) {
      return '<span class="iw-cart-drawer__upsell-money">' + cur + '</span>';
    }
    return (
      '<span class="iw-cart-drawer__upsell-money">' +
      cur +
      '</span><span class="iw-cart-drawer__upsell-compare-at">' +
      formatMoney(compare) +
      '</span>'
    );
  }

  function upsellAddingLabelText() {
    const w = window.IW_CART_DRAWER || {};
    return w.upsellAddingLabel || 'Adding...';
  }

  function setQtyOrRemoveLoading(el, loading) {
    if (!el) return;
    el.classList.toggle('is-loading', !!loading);
    el.disabled = !!loading;
  }

  function sectionsPayload() {
    const c = cfg();
    return {
      sections: [c.sectionId],
      sections_url: window.location.pathname || '/',
    };
  }

  function replaceCartDrawerSection(nextEl, eventDetail) {
    const c = cfg();
    if (!nextEl || !c.sectionId) return;
    const cur = document.getElementById('shopify-section-' + c.sectionId);
    if (!cur) return;

    var upsellPreserve = null;
    var oldDyn = cur.querySelector('[data-iw-upsell-dynamic]');
    var newDyn = nextEl.querySelector('[data-iw-upsell-dynamic]');
    if (oldDyn && newDyn && oldDyn.dataset.iwLoaded === '1') {
      var os = oldDyn.getAttribute('data-seed-product-id') || '';
      var ns = newDyn.getAttribute('data-seed-product-id') || '';
      if (os && os === ns) {
        upsellPreserve = oldDyn.innerHTML;
      }
    }

    const prevRoot = cur.querySelector('[data-iw-cart-drawer-root]');
    const keepOpen =
      (prevRoot && prevRoot.classList.contains('is-open')) ||
      document.body.classList.contains('iw-cart-drawer-open');

    /* Apply open state on the incoming tree before swap so the drawer never paints closed. */
    if (keepOpen) {
      const nextRoot = nextEl.querySelector('[data-iw-cart-drawer-root]');
      if (nextRoot) {
        nextRoot.classList.add('is-open');
        nextRoot.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('iw-cart-drawer-open');
    }

    cleanupAnnounceSlidersWithin(cur);
    cur.replaceWith(nextEl);
    bindDrawer();
    initAnnounceSliders();
    if (upsellPreserve != null) {
      var w = document.querySelector('[data-iw-upsell-dynamic]');
      if (w) {
        w.innerHTML = upsellPreserve;
        w.dataset.iwLoaded = '1';
      }
    }
    document.dispatchEvent(new CustomEvent('iw-cart:updated', { detail: eventDetail || {} }));
    if (keepOpen) open();
  }

  function applySectionsFromResponse(data) {
    if (!data || !data.sections) return;
    const c = cfg();
    let html = data.sections[c.sectionId];
    if (!html && data.sections && typeof data.sections === 'object') {
      const keys = Object.keys(data.sections);
      if (keys.length === 1) html = data.sections[keys[0]];
    }
    if (!html) return;
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    const next = tpl.content.firstElementChild;
    if (next) replaceCartDrawerSection(next, data);
  }

  function patchDrawerFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const root = document.querySelector('[data-iw-cart-drawer-root]');
    if (!root) return;

    cart.items.forEach(function (item) {
      const lines = root.querySelectorAll('[data-iw-cart-line]');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.getAttribute('data-line-key') !== item.key) continue;
        const input = line.querySelector('[data-iw-cart-qty-input]');
        if (input) input.value = String(item.quantity);
        const priceEl = line.querySelector('[data-iw-cart-line-price]');
        if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);
        break;
      }
    });

    const sub = root.querySelector('[data-iw-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(cart.total_price);

    const shipRow = root.querySelector('[data-iw-ship-row]');
    if (shipRow && shipRow.hasAttribute('data-iw-ship-threshold')) {
      const threshold = parseInt(shipRow.getAttribute('data-iw-ship-threshold'), 10) || 0;
      const msgEl = shipRow.querySelector('[data-iw-ship-msg]');
      const fill = shipRow.querySelector('[data-iw-progress-fill]');
      const bar = shipRow.querySelector('.iw-cart-drawer__progress');
      if (threshold > 0 && msgEl && fill && bar) {
        const subtotal = Math.round(Number(cart.total_price) || 0);
        const remaining = Math.max(0, threshold - subtotal);
        let progress = 0;
        if (threshold > 0) progress = Math.min(100, Math.round((subtotal * 100) / threshold));
        const progressTpl = shipRow.getAttribute('data-iw-ship-msg-progress') || '';
        const completeTpl = shipRow.getAttribute('data-iw-ship-msg-complete') || '';
        if (remaining > 0 && progressTpl) {
          msgEl.textContent = progressTpl.split('[remaining]').join(formatMoney(remaining));
        } else if (completeTpl) {
          msgEl.textContent = completeTpl;
        }
        fill.style.width = progress + '%';
        bar.setAttribute('aria-valuenow', String(progress));
      }
    }

    document.dispatchEvent(new CustomEvent('iw-cart:updated', { detail: { cart: cart } }));
  }

  function cleanupAnnounceSlidersWithin(container) {
    if (!container || !container.querySelectorAll) return;
    container.querySelectorAll('[data-iw-announce-slider]').forEach(function (track) {
      if (track._iwAnnounceIv) {
        clearInterval(track._iwAnnounceIv);
        track._iwAnnounceIv = null;
      }
      track.removeAttribute('data-iw-announce-bound');
    });
  }

  function initAnnounceSliders() {
    document.querySelectorAll('[data-iw-announce-slider]').forEach(function (track) {
      if (track.dataset.iwAnnounceBound === '1') return;
      const slides = track.querySelectorAll('.iw-cart-drawer__announce-slide');
      if (!slides.length) return;
      track.dataset.iwAnnounceBound = '1';
      slides.forEach(function (s, j) {
        s.setAttribute('aria-hidden', j === 0 ? 'false' : 'true');
      });
      if (slides.length < 2) return;
      let i = 0;
      const ms = parseInt(track.getAttribute('data-iw-announce-interval') || '4500', 10);
      track._iwAnnounceIv = setInterval(function () {
        slides[i].classList.remove('is-active');
        slides[i].setAttribute('aria-hidden', 'true');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
        slides[i].setAttribute('aria-hidden', 'false');
      }, ms);
    });
  }

  function formToAddItems(form) {
    const fd = new FormData(form);
    const id = fd.get('id');
    if (!id) return null;
    const item = { id: Number(id), quantity: Math.max(1, parseInt(fd.get('quantity') || '1', 10) || 1) };
    const selling = fd.get('selling_plan');
    if (selling) item.selling_plan = Number(selling);
    const props = {};
    for (const [k, v] of fd.entries()) {
      if (!k || typeof k !== 'string') continue;
      const m = k.match(/^properties\[(.+)\]$/);
      if (m) props[m[1]] = v;
    }
    if (Object.keys(props).length) item.properties = props;
    return { items: [item] };
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw j;
        return j;
      });
    });
  }

  function addFromForm(form) {
    const parsed = formToAddItems(form);
    if (!parsed) return Promise.reject(new Error('No variant id'));
    const body = Object.assign(parsed, sectionsPayload());
    const trybe = window.IWTrybeParams;
    if (trybe && typeof trybe === 'object') {
      const attrs = {};
      if (trybe.trybe) attrs.trybe = trybe.trybe;
      if (trybe.trybe_adid) attrs.trybe_adid = trybe.trybe_adid;
      if (Object.keys(attrs).length) body.attributes = attrs;
    }
    const subs = form.querySelectorAll('[type="submit"]');
    subs.forEach(function (b) {
      b.disabled = true;
    });
    return postJson(rootPath() + 'cart/add.js', body)
      .then(function (data) {
        applySectionsFromResponse(data);
        open();
        return data;
      })
      .catch(function (err) {
        const msg =
          (err && err.description) || (err && err.message) || 'Could not add to cart';
        window.alert(msg);
        throw err;
      })
      .finally(function () {
        subs.forEach(function (b) {
          b.disabled = false;
        });
      });
  }

  function changeLine(lineKey, quantity) {
    const q = Math.max(0, parseInt(quantity, 10) || 0);
    const body = { id: lineKey, quantity: q };
    if (q === 0) {
      Object.assign(body, sectionsPayload());
    }
    return postJson(rootPath() + 'cart/change.js', body).then(function (data) {
      if (data && data.sections) {
        applySectionsFromResponse(data);
      } else if (data && data.items) {
        patchDrawerFromCart(data);
      }
      return data;
    });
  }

  function changeSellingPlan(lineIndex, quantity, sellingPlanId) {
    const body = Object.assign(
      {
        line: Number(lineIndex),
        quantity: Math.max(1, parseInt(quantity, 10) || 1),
        selling_plan: sellingPlanId ? Number(sellingPlanId) : null,
      },
      sectionsPayload()
    );
    return postJson(rootPath() + 'cart/change.js', body).then(function (data) {
      if (data && data.sections) {
        applySectionsFromResponse(data);
      } else if (data && data.items) {
        patchDrawerFromCart(data);
      }
      return data;
    });
  }

  /** Sync drawer markup to server cart via Section Rendering API (no cart/update or noop change). */
  function syncDrawerHtml() {
    const c = cfg();
    if (!c.sectionId) return Promise.resolve();
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('section_id', c.sectionId);
      return fetch(u.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      })
        .then(function (r) {
          if (!r.ok) throw new Error('section');
          return r.text();
        })
        .then(function (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          let next = doc.getElementById('shopify-section-' + c.sectionId);
          if (!next) {
            const cand = doc.querySelector('[id^="shopify-section-"]');
            if (cand) next = cand;
          }
          if (next) {
            replaceCartDrawerSection(document.importNode(next, true), { sectionFetch: true });
          }
        })
        .catch(function () {});
    } catch (e) {
      return Promise.resolve();
    }
  }

  function getDrawerEls() {
    const r = document.querySelector('[data-iw-cart-drawer-root]');
    return {
      root: r,
      panel: r && r.querySelector('[data-iw-cart-panel]'),
    };
  }

  function open() {
    const { root } = getDrawerEls();
    if (!root) return;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('iw-cart-drawer-open');
    initAnnounceSliders();
    loadDynamicUpsellIfNeeded();
  }

  function close() {
    const { root, panel } = getDrawerEls();
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('iw-cart-drawer-open');
    if (panel && typeof panel.focus === 'function') {
      try {
        panel.focus({ preventScroll: true });
      } catch (e) {}
    }
  }

  function loadDynamicUpsellIfNeeded() {
    const c = cfg();
    if (!c || c.upsellMode !== 'dynamic') return;
    const wrap = document.querySelector('[data-iw-upsell-dynamic]');
    if (!wrap || wrap.dataset.iwLoaded === '1') return;
    const seed = wrap.getAttribute('data-seed-product-id');
    if (!seed) return;
    const limit = parseInt(wrap.getAttribute('data-limit') || '3', 10) || 3;
    const intent = wrap.getAttribute('data-intent') || 'related';
    const url =
      rootPath() +
      'recommendations/products.json?product_id=' +
      encodeURIComponent(seed) +
      '&limit=' +
      limit +
      '&intent=' +
      encodeURIComponent(intent);
    fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        const products = data.products || [];
        if (!products.length) return;
        wrap.innerHTML = '';
        products.forEach(function (p) {
          const row = buildDynamicUpsellRow(p);
          if (row) wrap.appendChild(row);
        });
        wrap.dataset.iwLoaded = '1';
      })
      .catch(function () {});
  }

  function buildDynamicUpsellRow(product) {
    if (!product || !product.variants || !product.variants.length) return null;

    const variants = product.variants;
    const v = variants.find(function (x) {
      return x.available;
    }) || variants[0];
    if (!v) return null;

    const vid = v.id;
    const row = document.createElement('div');
    row.className = 'iw-cart-drawer__upsell-row';
    row.setAttribute('data-iw-upsell-row', '');

    const img =
      product.featured_image ||
      (product.images && product.images[0] && product.images[0]) ||
      '';

    const media = document.createElement('div');
    media.className = 'iw-cart-drawer__upsell-media';
    if (img) {
      const a = document.createElement('a');
      a.href = product.url || '#';
      const im = document.createElement('img');
      im.src = typeof img === 'string' ? img : img.src;
      im.alt = product.title || '';
      im.width = 80;
      im.height = 80;
      a.appendChild(im);
      media.appendChild(a);
    }

    const info = document.createElement('div');
    info.className = 'iw-cart-drawer__upsell-info';
    const ta = document.createElement('a');
    ta.className = 'iw-cart-drawer__upsell-title';
    ta.href = product.url || '#';
    ta.textContent = product.title || '';
    const price = document.createElement('div');
    price.className = 'iw-cart-drawer__upsell-price';
    price.setAttribute('data-iw-upsell-price', '');
    const cmp0 =
      v.compare_at_price != null ? parseInt(v.compare_at_price, 10) || 0 : 0;
    const pri0 = parseInt(v.price, 10) || 0;
    price.innerHTML = renderUpsellPriceMarkup(pri0, cmp0);
    info.appendChild(ta);
    info.appendChild(price);

    const actions = document.createElement('div');
    actions.className = 'iw-cart-drawer__upsell-actions';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'iw-cart-drawer__upsell-add';
    btn.setAttribute('data-iw-upsell-add', '');
    btn.dataset.variantId = String(vid);
    const addLbl = cfg().upsellAddLabel || 'ADD';
    btn.dataset.iwDefaultLabel = addLbl;
    btn.textContent = addLbl;
    actions.appendChild(btn);

    row.appendChild(media);
    row.appendChild(info);
    row.appendChild(actions);

    if (variants.length > 1) {
      const variantWrap = document.createElement('div');
      variantWrap.className = 'iw-cart-drawer__upsell-variant';
      const sel = document.createElement('select');
      sel.className = 'iw-cart-drawer__upsell-select';
      sel.setAttribute('data-iw-upsell-select', '');
      variants.forEach(function (variant) {
        const o = document.createElement('option');
        o.value = String(variant.id);
        o.dataset.price = String(variant.price);
        o.dataset.compareAtPrice = String(variant.compare_at_price != null ? variant.compare_at_price : 0);
        o.textContent = variant.title || '';
        if (!variant.available) o.disabled = true;
        if (String(variant.id) === String(vid)) o.selected = true;
        sel.appendChild(o);
      });
      variantWrap.appendChild(sel);
      row.appendChild(variantWrap);
    }

    return row;
  }

  function upsellAdd(variantId, btnEl) {
    if (!variantId) return Promise.reject(new Error('No variant'));
    if (btnEl && (btnEl.classList.contains('is-busy') || btnEl.disabled)) {
      return Promise.reject(new Error('Busy'));
    }

    const adding = upsellAddingLabelText();
    if (btnEl) {
      btnEl.disabled = true;
      btnEl.classList.add('is-busy');
      if (!btnEl.dataset.iwDefaultLabel) {
        btnEl.dataset.iwDefaultLabel =
          btnEl.getAttribute('data-iw-default-label') ||
          (btnEl.textContent || '').trim() ||
          cfg().upsellAddLabel ||
          'ADD';
      }
      btnEl.textContent = adding;
    }

    const body = Object.assign(
      { items: [{ id: Number(variantId), quantity: 1 }] },
      sectionsPayload()
    );
    return postJson(rootPath() + 'cart/add.js', body)
      .then(function (data) {
        applySectionsFromResponse(data);
        open();
        return data;
      })
      .finally(function () {
        if (btnEl && btnEl.isConnected) {
          btnEl.disabled = false;
          btnEl.classList.remove('is-busy');
          btnEl.textContent = btnEl.dataset.iwDefaultLabel || cfg().upsellAddLabel || 'ADD';
        }
      });
  }

  function bindDrawer() {
    const { root } = getDrawerEls();
    if (!root || root.getAttribute('data-iw-bound') === '1') return;
    root.setAttribute('data-iw-bound', '1');

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-iw-cart-close]')) {
        e.preventDefault();
        close();
      }
    });

    root.addEventListener('click', function (e) {
      const rm = e.target.closest('[data-iw-cart-remove]');
      if (!rm) return;
      e.preventDefault();
      if (rm.classList.contains('is-loading')) return;
      const key = rm.getAttribute('data-line-key');
      setQtyOrRemoveLoading(rm, true);
      changeLine(key, 0)
        .catch(function () {})
        .finally(function () {
          if (rm.isConnected) setQtyOrRemoveLoading(rm, false);
        });
    });

    root.addEventListener('click', function (e) {
      const q = e.target.closest('[data-iw-cart-qty-change]');
      if (!q) return;
      e.preventDefault();
      if (q.classList.contains('is-loading')) return;
      const row = q.closest('[data-iw-cart-line]');
      const input = row && row.querySelector('[data-iw-cart-qty-input]');
      const key = row && row.getAttribute('data-line-key');
      if (!input || !key) return;
      let v = parseInt(input.value, 10) || 0;
      if (q.getAttribute('data-direction') === 'minus') v -= 1;
      else v += 1;
      if (v < 0) v = 0;
      setQtyOrRemoveLoading(q, true);
      changeLine(key, v)
        .catch(function () {})
        .finally(function () {
          if (q.isConnected) setQtyOrRemoveLoading(q, false);
        });
    });

    root.addEventListener(
      'change',
      function (e) {
        const sellingSel = e.target.closest('[data-iw-cart-selling-plan]');
        if (sellingSel) {
          if (sellingSel.disabled || sellingSel.classList.contains('is-loading')) return;
          const line = sellingSel.getAttribute('data-line');
          const row = sellingSel.closest('[data-iw-cart-line]');
          const qtyInput = row && row.querySelector('[data-iw-cart-qty-input]');
          const qty =
            (qtyInput && qtyInput.value) || sellingSel.getAttribute('data-quantity') || '1';
          const nextValue = sellingSel.value;
          const prevValue = sellingSel.getAttribute('data-prev-value');
          if (String(nextValue) === String(prevValue == null ? '' : prevValue)) return;
          sellingSel.classList.add('is-loading');
          sellingSel.disabled = true;
          changeSellingPlan(line, qty, nextValue)
            .then(function () {
              sellingSel.setAttribute('data-prev-value', nextValue);
            })
            .catch(function (err) {
              const msg =
                (err && err.description) ||
                (err && err.message) ||
                'Could not update purchase type';
              window.alert(msg);
              if (prevValue != null) sellingSel.value = prevValue;
            })
            .finally(function () {
              if (sellingSel.isConnected) {
                sellingSel.classList.remove('is-loading');
                sellingSel.disabled = false;
              }
            });
          return;
        }

        const sel = e.target.closest('[data-iw-upsell-select]');
        if (!sel) return;
        const row = sel.closest('[data-iw-upsell-row]');
        const btn = row && row.querySelector('[data-iw-upsell-add]');
        const priceEl = row && row.querySelector('[data-iw-upsell-price]');
        const opt = sel.options[sel.selectedIndex];
        if (btn) btn.dataset.variantId = sel.value;
        if (priceEl && opt && opt.dataset.price != null) {
          const p = parseInt(opt.dataset.price, 10) || 0;
          const cmp = parseInt(opt.dataset.compareAtPrice || '0', 10) || 0;
          priceEl.innerHTML = renderUpsellPriceMarkup(p, cmp);
        }
      },
      true
    );

    root.addEventListener('click', function (e) {
      const purchaseBtn = e.target.closest(
        '[data-iw-cart-upgrade-sub], [data-iw-cart-downgrade-sub]'
      );
      if (!purchaseBtn) return;
      e.preventDefault();
      if (purchaseBtn.classList.contains('is-loading') || purchaseBtn.disabled) return;
      const line = purchaseBtn.getAttribute('data-line');
      const isDowngrade = purchaseBtn.hasAttribute('data-iw-cart-downgrade-sub');
      const planId = isDowngrade ? '' : purchaseBtn.getAttribute('data-selling-plan');
      const row = purchaseBtn.closest('[data-iw-cart-line]');
      const qtyInput = row && row.querySelector('[data-iw-cart-qty-input]');
      const qty =
        (qtyInput && qtyInput.value) || purchaseBtn.getAttribute('data-quantity') || '1';
      if (!line) return;
      if (!isDowngrade && !planId) return;
      purchaseBtn.classList.add('is-loading');
      purchaseBtn.disabled = true;
      changeSellingPlan(line, qty, planId)
        .catch(function (err) {
          const msg =
            (err && err.description) ||
            (err && err.message) ||
            (isDowngrade
              ? 'Could not switch to one-time purchase'
              : 'Could not upgrade to subscription');
          window.alert(msg);
        })
        .finally(function () {
          if (purchaseBtn.isConnected) {
            purchaseBtn.classList.remove('is-loading');
            purchaseBtn.disabled = false;
          }
        });
    });

    root.addEventListener('click', function (e) {
      const add = e.target.closest('[data-iw-upsell-add]');
      if (!add) return;
      e.preventDefault();
      const vid = add.getAttribute('data-variant-id');
      upsellAdd(vid, add).catch(function () {});
    });

    bindCheckoutForm(root);
  }

  function checkoutLoadingText() {
    const w = window.IW_CART_DRAWER || {};
    return w.checkoutLoadingLabel || 'Checking out...';
  }

  function bindCheckoutForm(root) {
    const form = root.querySelector('.iw-cart-drawer__checkout-form');
    if (!form || form.getAttribute('data-iw-checkout-bound') === '1') return;
    form.setAttribute('data-iw-checkout-bound', '1');
    form.addEventListener('submit', function (e) {
      const btn = form.querySelector('[data-iw-checkout-btn]');
      if (!btn) return;
      if (btn.classList.contains('is-busy')) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      btn.classList.add('is-busy');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      const labelEl = form.querySelector('[data-iw-checkout-label]');
      if (labelEl) labelEl.textContent = checkoutLoadingText();
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          form.submit();
        });
      });
    });
  }

  function initSubmitCapture() {
    if (window.__iwCartSubmitCapture) return;
    window.__iwCartSubmitCapture = true;
    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('data-iw-native-add') === 'true') return;
        if (form.closest('[data-iw-cart-drawer-root]')) return;
        const action = (form.getAttribute('action') || '').toLowerCase();
        if (action.indexOf('/cart/add') === -1) return;
        if (!document.querySelector('[data-iw-cart-drawer-root]')) return;
        e.preventDefault();
        e.stopPropagation();
        addFromForm(form).catch(function () {});
      },
      true
    );
  }

  /**
   * Capture phase + stopPropagation: Webflow w-nav handlers use preventDefault/stopPropagation
   * on bubble; opening before sync keeps the drawer responsive if cart API is slow.
   */
  function initCartLinks() {
    if (window.__iwCartLinksCapture) return;
    window.__iwCartLinksCapture = true;
    document.addEventListener(
      'click',
      function (e) {
        const t = e.target.closest('[data-iw-cart-open], .js-iw-cart-open');
        if (!t) return;
        if (!document.querySelector('[data-iw-cart-drawer-root]')) return;
        e.preventDefault();
        e.stopPropagation();
        open();
        syncDrawerHtml();
      },
      true
    );
  }

  /** Backup: intercept fetch to /cart/add.js */
  function initFetchWrap() {
    if (window.__iwCartFetchWrapped) return;
    window.__iwCartFetchWrapped = true;
    const nativeFetch = window.fetch;
    if (typeof nativeFetch !== 'function') return;
    window.fetch = function () {
      const args = arguments;
      const req = args[0];
      const url = typeof req === 'string' ? req : req && req.url;
      return nativeFetch.apply(this, args).then(function (res) {
        try {
          var iwRoot = document.querySelector('[data-iw-cart-drawer-root]');
          if (
            url &&
            url.indexOf('/cart/add') !== -1 &&
            url.indexOf('.js') !== -1 &&
            res.ok &&
            iwRoot
          ) {
            res
              .clone()
              .json()
              .then(function (data) {
                if (data && data.sections) applySectionsFromResponse(data);
                else syncDrawerHtml();
                open();
              })
              .catch(function () {});
          } else if (
            url &&
            url.indexOf('/cart/update') !== -1 &&
            url.indexOf('.js') !== -1 &&
            res.ok &&
            iwRoot
          ) {
            /* Do not replace the drawer from cart/update.js — avoids resetting upsells on theme qty flows. */
          }
          /* cart/change.js is handled in changeLine()'s response handler — avoid a second section swap here (flash / close-reopen). */
        } catch (err) {}
        return res;
      });
    };
  }

  function bindDrawerWhenReady() {
    if (document.querySelector('[data-iw-cart-drawer-root]')) bindDrawer();
  }

  function initEscapeClose() {
    if (window.__iwCartEscapeClose) return;
    window.__iwCartEscapeClose = true;
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Escape') return;
        const root = document.querySelector('[data-iw-cart-drawer-root]');
        if (!root || !root.classList.contains('is-open')) return;
        close();
      },
      true
    );
  }

  function boot() {
    initSubmitCapture();
    initCartLinks();
    initFetchWrap();
    initEscapeClose();
    bindDrawerWhenReady();
    initAnnounceSliders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('DOMContentLoaded', bindDrawerWhenReady);
  window.setTimeout(bindDrawerWhenReady, 0);

  window[NAME] = {
    open: open,
    close: close,
    refresh: syncDrawerHtml,
    addFromForm: addFromForm,
    changeLine: changeLine,
    changeSellingPlan: changeSellingPlan,
  };
})();